import { FormControl } from '@material-ui/core';
import { Autocomplete, AutocompleteRenderGroupParams } from '@material-ui/lab';
import { Select, TextInput } from '@room-match/shared-ui-components';
import Locs from 'data/Locations.json';
import { FormikProps } from 'formik';
import React, { useEffect, useMemo, useState } from 'react';
import { COLORS } from 'shared/constants/COLORS';
import { IAddress, IAddressRequestPayload } from 'shared/interfaces/IAddress';
import { countryStateApiService } from 'shared/services/countryStateApiService';
import { useStyles } from './LocationModal.styles';

type Props = {
  data: IAddress | null;
  form: FormikProps<IAddressRequestPayload>;
  country?: string;
};

interface Location {
  [key: string]: { [key: string]: string[] }[];
}

interface CityOption {
  state: string;
  city: string;
}

const { getStatesByCountry } = countryStateApiService();

const WorkingLocationModal: React.FC<Props> = ({ data, form, country: countryProps }) => {
  const classes = useStyles();
  const locationData: Location = Locs as any;
  const [country, setCountry] = useState<string>(
    countryProps && ['Australia', 'United States', 'New Zealand'].includes(countryProps) ? countryProps : '',
  );
  const { data: states } = getStatesByCountry(
    country === 'Australia' ? 'AU' : country === 'United States' ? 'US' : country === 'New Zealand' ? 'NZ' : '',
    {
      enabled: Boolean(country),
    },
  );
  const [city, setCity] = useState<CityOption | null>(null);
  const [zip, setZip] = useState<any>();

  const cityOptions = useMemo(() => {
    return (
      locationData[country]
        ?.flatMap(Object.entries)
        .map(([state, cities]) => cities.map((city: string) => ({ state, city }) as CityOption))
        .flat() || []
    );
  }, [country, locationData]);

  const renderGroup = (params: AutocompleteRenderGroupParams) => {
    return (
      <div key={params.key} style={{ paddingLeft: 10 }}>
        <li style={{ color: COLORS.PRIMARY }}>{params.group}</li>
        <ul>{params.children}</ul>
      </div>
    );
  };

  const handleCountryChange = (e: React.ChangeEvent<{ value: unknown }>) => {
    const val = e.target.value as string;
    console.log('val', val);

    const countryCode = val === 'Australia' ? 'AU' : val === 'United States' ? 'US' : val === 'New Zealand' ? 'NZ' : '';
    console.log('countryCode', countryCode);

    form.setFieldValue('country_code', countryCode);
    form.setFieldValue('address_type', 'other_address');

    form.setValues({
      country_code: countryCode,
      address_type: 'other_address',
      zip_code: '',
      city_suburb: '',
      state_code: '',
    });
    setCity(null);
    setZip('');

    setCountry(e.target.value as string);
  };
  const handleCityChange = (e: React.ChangeEvent<{}>, value: CityOption | null) => {
    const stateOfCity = cityOptions.find((cityOption) => cityOption.city === value?.city)?.state;
    const stateCode = states?.find((state) => state.name === stateOfCity)?.iso2;
    console.log('stateCode', stateCode, states);

    form.setFieldValue('state_code', stateCode || '');
    form.setFieldValue('city_suburb', value?.city || '');
    setCity(value);
  };

  const handleZipChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    //return if the input contains non-numeric characters
    if (e.target.value.match(/\D/)) return;
    form.setFieldValue('zip_code', e.target.value);
    setZip(e.target.value);
  };

  useEffect(() => {
    if (!data) return;

    const countryData =
      data.attributes.country_code === 'AU'
        ? 'Australia'
        : data.attributes.country_code === 'US'
          ? 'United States'
          : data.attributes.country_code === 'NZ'
            ? 'New Zealand'
            : '';

    const cities =
      locationData[countryData]
        ?.flatMap(Object.entries)
        .map(([state, cities]) => cities.map((city: string) => ({ state, city }) as CityOption))
        .flat() || [];

    if (!country) setCountry(countryData || '');

    setCity(cities.find((cityOption) => cityOption.city === data.attributes.city_suburb) || null);
    setZip(data.attributes.zip_code);

    form.setValues({
      ...data.attributes,
      address1: data.attributes.address1 || '',
      address2: data.attributes.address2 || '',
      location_name: data.attributes.location_name || '',
    });
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <FormControl fullWidth>
        <Select
          data-cy="country-dropdown"
          variant="outlined"
          id="country"
          value={country}
          className={classes.workingLocationSelect}
          onChange={handleCountryChange}
          label="Country"
          data={Object.keys(locationData).map((country) => ({
            key: country,
            value: country,
          }))}
          // IconComponent={SelectArrowDownIcon}
        />
      </FormControl>
      <Autocomplete
        data-cy="city-dropdown"
        disabled={!country}
        noOptionsText={country ? 'City is not on the list' : 'Please select a country first'}
        options={cityOptions}
        groupBy={(option) => option.state}
        getOptionLabel={(option) => option.city}
        value={city}
        classes={{ option: classes.option, popupIndicator: classes.popUpIndicator }}
        onChange={handleCityChange}
        renderGroup={renderGroup}
        renderInput={(params) => (
          <TextInput
            {...params}
            variant="outlined"
            autoComplete="none"
            // value={form.values.zip_code}
            placeholder="Select City"
            // onChange={(e) => {
            //   form.setFieldValue('zip_code', e.target.value);
            //   if (e.target.value.length > 3) {
            //     geocode(e.target.value);
            //   } else {
            //     setAddressList([]);
            //   }
            // }}
            label="City"
            inputProps={{
              ...params.inputProps,
              autocomplete: 'none',
              classes: {
                root: classes.textInputStyle,
              },
              form: {
                autocomplete: 'none',
              },
            }}
          />
        )}
      />

      {/* Zip Code Input */}
      <TextInput
        data-cy="zipcode"
        disabled={!city}
        variant="outlined"
        autoComplete="none"
        value={(city && country) || zip !== 0 ? zip : ''}
        onChange={handleZipChange}
        // value={form.values.zip_code}
        placeholder="Enter Zip Code"
        label="Zip Code"
        // error={Boolean(city && city && !zip)}
        // errorMessage={getErrorMessage(
        //   !zip,
        //   Boolean(city && city) ? 'Zip code is required' : 'Please select a city first',
        // )}
      />
    </div>
  );
};

export default WorkingLocationModal;
